import { AssetUrls } from 'config/assets';

// Files served from the public/ folder are same-origin, so the download
// attribute on an anchor forces a real download for these.
const PUBLIC = process.env.PUBLIC_URL || '';

export type AssetFormat = 'SVG' | 'PNG' | 'ICO';
export type PreviewBackground = 'light' | 'dark';

export interface BrandAsset {
  label: string;
  description?: string;
  href: string;
  format: AssetFormat;
  preview: string;
  previewBg: PreviewBackground;
  // true when the file is served same-origin (public/), so the browser
  // honors the download attribute directly.
  sameOrigin?: boolean;
  // true for small-intrinsic SVGs that should scale up to fill the card.
  scaleUp?: boolean;
}

export interface ColorToken {
  name: string;
  hex: string;
  usage: string;
  // text color to use when printing the hex on top of the swatch
  swatchText?: 'light' | 'dark';
}

export interface TypeFace {
  name: string;
  usage: string;
  source: string;
  sample: string;
  fontFamily: string;
}

// ---------------------------------------------------------------------------
// Logos
// ---------------------------------------------------------------------------
export const LOGO_ASSETS: BrandAsset[] = [
  {
    label: 'Logo, black',
    description: 'Black logo. For light backgrounds.',
    href: AssetUrls.logos.black,
    format: 'PNG',
    preview: AssetUrls.logos.black,
    previewBg: 'light',
  },
];

// The swirl mark on its own, in three colorways. These are icons, not lockups.
export const ICON_SET_ASSETS: BrandAsset[] = [
  {
    label: 'Icon, gold',
    description: 'The swirl mark in brand gold. Reads on dark backgrounds.',
    href: AssetUrls.logos.iconGold,
    format: 'SVG',
    preview: AssetUrls.logos.iconGold,
    previewBg: 'dark',
    scaleUp: true,
  },
  {
    label: 'Icon, white',
    description: 'The swirl mark in white. For dark backgrounds.',
    href: AssetUrls.logos.iconWhite,
    format: 'SVG',
    preview: AssetUrls.logos.iconWhite,
    previewBg: 'dark',
    scaleUp: true,
  },
  {
    label: 'Icon, black',
    description: 'The swirl mark in black. For light backgrounds.',
    href: AssetUrls.logos.iconBlack,
    format: 'SVG',
    preview: AssetUrls.logos.iconBlack,
    previewBg: 'light',
    scaleUp: true,
  },
];

// ---------------------------------------------------------------------------
// Banner lockups
// ---------------------------------------------------------------------------
export const BANNER_ASSETS: BrandAsset[] = [
  {
    label: 'Horizontal lockup, black',
    description: 'Icon plus trademagic.gg wordmark, horizontal.',
    href: `${PUBLIC}/logobanner.png`,
    format: 'PNG',
    preview: `${PUBLIC}/logobanner.png`,
    previewBg: 'light',
    sameOrigin: true,
  },
  {
    label: 'Social share banner',
    description: 'Wide banner for link previews and social cards.',
    href: AssetUrls.logos.bannerSocialShare,
    format: 'PNG',
    preview: AssetUrls.logos.bannerSocialShare,
    previewBg: 'dark',
  },
];

// ---------------------------------------------------------------------------
// App icons (platform files: store listings, manifests, browser tab, iOS)
// ---------------------------------------------------------------------------
export const APP_ICON_ASSETS: BrandAsset[] = [
  {
    label: 'App icon, 512px',
    description: 'PNG app icon for stores and manifests.',
    href: `${PUBLIC}/logo512.png`,
    format: 'PNG',
    preview: `${PUBLIC}/logo512.png`,
    previewBg: 'light',
    sameOrigin: true,
  },
  {
    label: 'App icon, 192px',
    description: 'Smaller PNG app icon.',
    href: `${PUBLIC}/logo192.png`,
    format: 'PNG',
    preview: `${PUBLIC}/logo192.png`,
    previewBg: 'light',
    sameOrigin: true,
  },
  {
    label: 'Apple touch icon',
    description: 'Home screen icon for iOS.',
    href: `${PUBLIC}/apple-touch-icon.png`,
    format: 'PNG',
    preview: `${PUBLIC}/apple-touch-icon.png`,
    previewBg: 'light',
    sameOrigin: true,
  },
  {
    label: 'Favicon',
    description: 'Browser tab icon.',
    href: `${PUBLIC}/favicon.ico`,
    format: 'ICO',
    preview: `${PUBLIC}/favicon.ico`,
    previewBg: 'light',
    sameOrigin: true,
  },
];

// ---------------------------------------------------------------------------
// Point component
// ---------------------------------------------------------------------------
// The point coin is hosted on the assets CDN. The arevakhach symbol on the
// coin is Unicode U+058D (Armenian eternity sign).
export const POINT_COIN_SRC = AssetUrls.icons.profile.coin;
export const POINT_GLYPH = '֍'; // ֍
export const POINT_GLYPH_CODEPOINT = 'U+058D';

// Exact values pulled from src/components/Points/Points.tsx and Points.css.
export const POINT_TOKENS = {
  numberColor: '#f5e2be',
  font: 'Georgia',
  smallFontSize: '11px',
  bigFontSize: '14px',
  smallCoinPx: 16,
  bigCoinPx: 22,
  pillGradient: 'linear-gradient(90deg, #f6e3bf, #634f21)',
  pillInner: 'rgba(0, 0, 0, 0.85)',
  pillRadius: '24px',
};

// Copy-pasteable recipe text for the simplified (text only) reproduction.
export const POINT_SIMPLIFIED_CSS = `/* TradeMagic point value, text reproduction */
font-family: Georgia, serif;
color: #f5e2be;
/* prefix the integer with the eternity glyph U+058D */
content: "\\058D";`;

// ---------------------------------------------------------------------------
// Color tokens
// ---------------------------------------------------------------------------
export const COLOR_TOKENS: ColorToken[] = [
  { name: 'Gold', hex: '#f5e2be', usage: 'Point value text', swatchText: 'dark' },
  { name: 'Gold, coin dark', hex: '#634f21', usage: 'Point pill gradient end', swatchText: 'light' },
  { name: 'Purple', hex: '#6f51e4', usage: 'Primary brand color, gradient start', swatchText: 'light' },
  { name: 'Accent', hex: '#e02cdc', usage: 'Accent, gradient end', swatchText: 'light' },
  { name: 'Dark purple', hex: '#201428', usage: 'Deep background', swatchText: 'light' },
  { name: 'Black', hex: '#0c0c0c', usage: 'Primary dark surface', swatchText: 'light' },
  { name: 'White', hex: '#ffffff', usage: 'Text and the white logo', swatchText: 'dark' },
  { name: 'Grey', hex: '#323232', usage: 'Secondary surface', swatchText: 'light' },
  { name: 'Darker grey', hex: '#181818', usage: 'Card surface', swatchText: 'light' },
  { name: 'Light grey', hex: '#878787', usage: 'Muted text', swatchText: 'dark' },
];

export const BRAND_GRADIENT = 'linear-gradient(90deg, #6F51E4 0%, #E02CDC 100%)';
export const BRAND_GRADIENT_CSS = 'linear-gradient(90deg, #6F51E4 0%, #E02CDC 100%)';

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------
export const TYPEFACES: TypeFace[] = [
  {
    name: 'Plus Jakarta Sans',
    usage: 'Interface text and body copy',
    source: 'Google Fonts',
    sample: 'Trade Magic the Gathering cards',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  {
    name: 'Crimson Pro',
    usage: 'Headings',
    source: 'Google Fonts',
    sample: 'Build your collection',
    fontFamily: "'Crimson Pro', serif",
  },
  {
    name: 'Beleren 2016 Bold',
    usage: 'Magic card text styling',
    source: 'Hosted on the assets CDN',
    sample: 'Black Lotus',
    fontFamily: "'Beleren2016', serif",
  },
  {
    name: 'Georgia',
    usage: 'Point value numerals',
    source: 'System font',
    sample: '1,250',
    fontFamily: 'Georgia, serif',
  },
];

// ---------------------------------------------------------------------------
// Usage do's and don'ts
// ---------------------------------------------------------------------------
export const USAGE_DOS: string[] = [
  'Keep clear space around the logo equal to the height of the icon.',
  'Place the white logo on dark backgrounds and the black logo on light backgrounds.',
  'Keep the logo at least 24 pixels tall so the icon stays legible.',
  'Reproduce the point value with the exact gold and Georgia font shown below.',
];

export const USAGE_DONTS: string[] = [
  'Do not recolor the gold logo or the point value.',
  'Do not stretch, squash, or rotate the logo.',
  'Do not place the white logo on a light background or the black logo on a dark background.',
  'Do not add shadows, outlines, or effects to the logo.',
];

// ---------------------------------------------------------------------------
// Assets not yet available. Listed honestly rather than approximated.
// ---------------------------------------------------------------------------
export const DEFERRED_ASSETS: string[] = [
  'Standalone TradeMagic wordmark, separate from the icon lockup',
  'Trade on TradeMagic button assets, default and hover states',
  'Banner lockup as SVG (only PNG is available today)',
];
